import { logAPIResponse, logPOSEvent } from "../../utils/backgroundLogger";

type SrmErr = { id?: string; mess?: string; message?: string; codRetour?: string; noTrans?: string };

const errorLists = (response: any): SrmErr[] => {
  const lists = [
    response?.retourTrans?.retourTransActu?.listErr,
    response?.retourTrans?.retourTransLot?.listErr,
    response?.retourTrans?.listErr,
    response?.listErr,
  ].filter((list: unknown) => Array.isArray(list));
  return lists.flat();
};

/** User-facing WEB-SRM error: identifier, message, and return code. */
export const srmErrorText = (response: unknown): string => errorLists(response)
  .map((err) => [err.id, err.mess || err.message, err.codRetour].map((part) => String(part || "").trim()).filter(Boolean).join(" · "))
  .filter(Boolean)
  .join("\n");

const reducedRequest = (request: any) => {
  const actu = request?.reqTrans?.transActu || {};
  return {
    reqTrans: {
      transActu: {
        noTrans: String(actu.noTrans || ""),
        typTrans: String(actu.typTrans || ""),
        mont: { apresTax: String(actu.mont?.apresTax || "") },
      },
    },
  };
};

const reducedResponse = (response: any, failure: string) => {
  const actu = response?.retourTrans?.retourTransActu || {};
  const errors = errorLists(response).map((err) => ({
    id: String(err.id || ""),
    mess: String(err.mess || err.message || ""),
    codRetour: String(err.codRetour || ""),
    ...(err.noTrans ? { noTrans: String(err.noTrans) } : {}),
  }));
  if (!errors.length && failure) {
    errors.push({ id: "", mess: failure, codRetour: "" });
  }
  const retourActu: Record<string, unknown> = {
    noTrans: String(actu.noTrans || ""),
    psiNoTrans: String(actu.psiNoTrans || ""),
    psiDatTrans: String(actu.psiDatTrans || ""),
  };
  if (errors.length) retourActu.listErr = errors;
  return { retourTrans: { retourTransActu: retourActu } };
};

/**
 * Writes the WEB-SRM result the SRS must keep.
 * Errors keep the identifier, message, and return code.
 * A success keeps the returned transaction number and time.
 */
export async function logSrmExchange(args: {
  url?: string;
  request: unknown;
  response: unknown;
  ok: boolean;
  status?: number;
  duration?: number;
  failure?: string;
}): Promise<void> {
  const failure = String(args.failure || "");
  const response = reducedResponse(args.response, args.ok ? "" : failure);
  const errors = ((response.retourTrans.retourTransActu.listErr as SrmErr[]) || []);
  try {
    await logAPIResponse(
      String(args.url || "WEB-SRM"),
      "POST",
      reducedRequest(args.request),
      response,
      Number(args.duration || 0),
      args.ok ? Number(args.status || 200) : Number(args.status || 400),
    );
  } catch (error) {
    console.error("[SRM] Failed to log API response", error);
  }
  for (const err of errors) {
    try {
      await logPOSEvent("WEB_SRM_ERROR", {
        id: err.id || "",
        mess: err.mess || "",
        codRetour: err.codRetour || "",
        noTrans: err.noTrans || "",
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error("[SRM] Failed to log WEB-SRM error", error);
    }
  }
}
